<template>
  <b-row>
    <b-col sm="3">
      <strong>How to Apply</strong>
      <p>Select all of the methods that students can use to apply for opportunities at this site. The settings on this site will be the default for any opportunities created for this site, but they can be changed on those opportunities.</p>
    </b-col>
    <b-col sm="9">
      <b-alert :show="!validApplicationTypes.length && !opportunity.opportunityTypeId" variant="warning">
        To complete this portion of the form, you must first select a Type and Category in the Details tab.
      </b-alert>

      <b-alert :show="!validApplicationTypes.length && !!opportunity.opportunityTypeId" variant="warning">
        <span v-if="opportunity && opportunity.opportunityType">
          There are no options available for this opportunity type ({{ opportunity.opportunityType.name }})
        </span>
      </b-alert>

      <b-form>
        <b-form-group label="How To Apply" label-class="control-label opportunity-form-label">
          <div class="checkbox" v-for="applicationType in validApplicationTypes" :key="applicationType.applicationTypeId">
            <b-form-checkbox                         
                          v-model="opportunityApplicationTypes"
                          :value="{applicationTypeId: applicationType.applicationTypeId,
                                    applicationType: applicationType}">
            {{applicationType.description}}
            </b-form-checkbox>
          </div>
        </b-form-group>

        <b-form-group label="Application URL" label-class="control-label opportunity-form-label" :class="{ 'invalid-group': $v.applicationUrl.$error }">
          <b-form-input v-model="applicationUrl" type="text" :class="{ 'is-invalid': $v.applicationUrl.$error }"></b-form-input>
          <span class="span-feedback invalid-feedback" v-if="$v.applicationUrl.$error">You must enter an application URL.</span>
        </b-form-group>

        <div v-if="requiresSiteContact">

          <b-alert :show="!siteId" variant="danger">You must select a site to select contacts for this opportunity.</b-alert>
          <div v-if="siteId">
            <b-alert show variant="info">You can select multiple contacts for this opportunity. Students will be given the contact information when they indicate their intent to apply to this opportunity.</b-alert>
            <b-alert :show="!siteContacts.length" variant="warning">This site has no contacts.</b-alert>


            <b-form-group label="Contacts" label-class="control-label opportunity-form-label">
              <div class="checkbox" v-for="contact in siteContacts" v-bind:key="contact.siteContactId">
                <b-form-checkbox                         
                              v-model="opportunitySiteContacts" :value="{siteContactId: contact.siteContactId}">
                    <strong>{{contact.firstName}} {{contact.lastName}}</strong><br/>    
                    {{contact.title}}<br />
                    Authorized Signer: {{contact.authorizedSigner | trueOrFalse}}<br />
                    Placements Contact: {{contact.studentContact | trueOrFalse}}<br />
                    <b-alert show variant="warning" v-if="!contact.active">This contact is inactive. If the contact is no longer needed for students they may be removed.</b-alert>
                </b-form-checkbox>
              </div>
            </b-form-group>          
          </div> 
        </div>

        <slot name="buttons"></slot>
      </b-form>
    </b-col>
  </b-row>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {};
  },

  props: {
    dirty: {
      type: Boolean
    },

    opportunity: {
      type: Object
    },

    applicationTypes: {
      type: Array,
      required: true
    }
  },

  watch: {
    dirty: function() {
      if (this.dirty) {
        this.$v.$touch();
      }
    },

    "$v.$error": function() {
      var validState = true;
      if (this.$v.$error) {
        validState = false;
      }
      this.$store.commit("updateApplyValidation", validState);
    },

    opportunity: function() {
      this.opportunityApplicationTypes = [];
      this.opportunitySiteContacts = [];

      this.applicationUrl = this.opportunity.applicationUrl;

      var oat = [];
      _.forEach(this.opportunity.opportunityApplicationTypes, function(
        opportunityApplicationType
      ) {
        oat.push({
          applicationTypeId: opportunityApplicationType.applicationTypeId,
          applicationType: opportunityApplicationType.applicationType
        });
      });
      this.opportunityApplicationTypes = oat;

      var osc = [];
      _.forEach(this.opportunity.opportunitySiteContacts, function(
        opportunitySiteContact
      ) {
        osc.push({
          siteContactId: opportunitySiteContact.siteContactId
        });
      });
      this.opportunitySiteContacts = osc;
    },

    // remove any application types that are no longer valid
    validApplicationTypes: function() {
      var that = this;
      if (this.validApplicationTypes.length) {
        _.forEach(this.opportunityApplicationTypes, function(
          opportunityApplicationType
        ) {
          var foundMatch = _.find(that.validApplicationTypes, function(
            validApplicationType
          ) {
            return (
              validApplicationType.applicationTypeId ===
              opportunityApplicationType.applicationTypeId
            );
          });
          if (!foundMatch) {
            that.opportunityApplicationTypes = _.reject(
              that.opportunityApplicationTypes,
              function(oat) {
                return (
                  oat.applicationTypeId ===
                  opportunityApplicationType.applicationTypeId
                );
              }
            );
          }
        });
      }
    }
  },

  validations: function() {
    var validate = { applicationUrl: {} };
    if (this.requiresApplicationUrl) {
      validate.applicationUrl = {
        required
      };
    }
    return validate;
  },

  computed: {
    opportunityApplicationTypes: {
      get() {
        return this.$store.state.opportunityForm.opportunity
          .opportunityApplicationTypes;
      },
      set(value) {
        this.$store.commit("updateOpportunityApplicationTypes", value);
      }
    },

    opportunitySiteContacts: {
      get() {
        return this.$store.state.opportunityForm.opportunity
          .opportunitySiteContacts;
      },
      set(value) {
        this.$store.commit("updateOpportunitySiteContacts", value);
      }
    },

    siteContacts: function() {
      return this.$store.state.opportunityForm.siteContacts;
    },

    // only show additional materials that match the selected type and category
    validApplicationTypes: function() {
      var applicationTypeArray = [];
      var selectedOpportunityTypeId = this.$store.state.opportunityForm
        .opportunity.opportunityTypeId;
      var selectedOpportunityCategoryId = this.$store.state.opportunityForm
        .opportunity.opportunityCategoryId;
      _.forEach(this.applicationTypes, function(applicationType) {
        var foundMatch = _.find(
          applicationType.applicationTypeOpportunityTypeOpportunityCategories,
          function(applicationTypeOpportunityTypeOpportunityCategory) {
            return (
              applicationTypeOpportunityTypeOpportunityCategory.opportunityTypeId ===
                selectedOpportunityTypeId &&
              applicationTypeOpportunityTypeOpportunityCategory.opportunityCategoryId ===
                selectedOpportunityCategoryId
            );
          }
        );
        if (foundMatch) {
          applicationTypeArray.push(applicationType);
        }
      });
      return applicationTypeArray;
    },

    // if any of the application types selected require application url, return true
    requiresApplicationUrl: function() {
      var isRequired = false;
      _.forEach(this.opportunityApplicationTypes, function(
        opportunityApplicationType
      ) {
        if (opportunityApplicationType.applicationType.requiresApplicationUrl) {
          isRequired = true;
        }
      });
      return isRequired;
    },

    // if any of the application types selected require a site contact url, return true
    requiresSiteContact: function() {
      var isRequired = false;
      _.forEach(this.opportunityApplicationTypes, function(
        opportunityApplicationType
      ) {
        if (opportunityApplicationType.applicationType.requiresSiteContact) {
          isRequired = true;
        }
      });
      return isRequired;
    },

    siteId: function() {
      return this.$store.state.opportunityForm.opportunity.siteId;
    },

    applicationUrl: {
      get() {
        return this.$store.state.opportunityForm.opportunity.applicationUrl;
      },
      set(value) {
        this.$store.commit("updateApplicationUrl", value);
      }
    }
  },

  created() {
    this.opportunityApplicationTypes = [];
    this.opportunitySiteContacts = [];

    if (this.opportunity) {
      this.applicationUrl = this.opportunity.applicationUrl;

      var oat = [];
      _.forEach(this.opportunity.opportunityApplicationTypes, function(
        opportunityApplicationType
      ) {
        oat.push({
          applicationTypeId: opportunityApplicationType.applicationTypeId,
          applicationType: opportunityApplicationType.applicationType
        });
      });
      this.opportunityApplicationTypes = oat;

      var osc = [];
      _.forEach(this.opportunity.opportunitySiteContacts, function(
        opportunitySiteContact
      ) {
        osc.push({
          siteContactId: opportunitySiteContact.siteContactId
        });
      });
      this.opportunitySiteContacts = osc;
    }
  }
};
</script>

<style scoped>
</style>
